/**
 * Campaign + agent lists for dashboard widget configuration (same sources as Status / Call History).
 */
import { useState, useEffect, useMemo } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { visibleCampaignTitlesForStatusSidebar } from './campaignFilterVisibility';
import type { CampaignFilterVisibilityConfig } from './campaignFilterVisibility';
import { parseAgentDirectoryPayload } from './agentDirectory';

export type CampaignCatalogRow = {
  campaign_id: number;
  campaign_title: string | null;
  campaign_group_name: string | null;
};

export type AgentCatalogEntry = {
  agent_id: number;
  display_name: string;
};

function workerOriginFromWs(ws: string): string {
  if (!ws.startsWith('ws')) return '';
  try {
    const normalized = ws.startsWith('wss://')
      ? `https://${ws.slice('wss://'.length)}`
      : `http://${ws.slice('ws://'.length)}`;
    return new URL(normalized).origin;
  } catch {
    return '';
  }
}

export function useWidgetCatalog() {
  const { getToken } = useAuth();
  const [catalogRows, setCatalogRows] = useState<CampaignCatalogRow[]>([]);
  const [agents, setAgents] = useState<AgentCatalogEntry[]>([]);
  const [teams, setTeams] = useState<string[]>([]);
  const [visibility, setVisibility] = useState<CampaignFilterVisibilityConfig | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const token = await getToken();
      if (!token || cancelled) return;
      const base = typeof window !== 'undefined' ? window.location.origin : '';
      try {
        const cfgRes = await fetch(`${base}/api/config?keys=presence_ws_url,campaign_filter_visibility`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        let workerOrigin = '';
        let vis: CampaignFilterVisibilityConfig | null = null;
        if (cfgRes.ok) {
          const cfg = await cfgRes.json();
          const ws = typeof cfg.presence_ws_url === 'string' ? cfg.presence_ws_url.trim() : '';
          workerOrigin = workerOriginFromWs(ws);
          if (cfg.campaign_filter_visibility && typeof cfg.campaign_filter_visibility === 'object') {
            vis = cfg.campaign_filter_visibility as CampaignFilterVisibilityConfig;
          }
        }
        if (!cancelled) setVisibility(vis);

        if (workerOrigin) {
          const [campRes, agentRes] = await Promise.all([
            fetch(`${workerOrigin}/campaigns/catalog`),
            fetch(`${workerOrigin}/agents/catalog`),
          ]);
          if (!cancelled && campRes.ok) {
            const data = (await campRes.json()) as { campaigns?: CampaignCatalogRow[] };
            if (Array.isArray(data.campaigns)) setCatalogRows(data.campaigns);
          }
          if (!cancelled && agentRes.ok) {
            const data = (await agentRes.json()) as { agents?: { agent_id: number; payload: string }[] };
            const mapped: AgentCatalogEntry[] = [];
            const teamSet = new Set<string>();
            for (const r of data.agents ?? []) {
              const row = parseAgentDirectoryPayload(r.agent_id, r.payload);
              if (row) {
                mapped.push({ agent_id: r.agent_id, display_name: row.name });
                const team = row.team?.trim();
                if (team) teamSet.add(team);
              }
            }
            mapped.sort((a, b) => a.display_name.localeCompare(b.display_name, undefined, { sensitivity: 'base' }));
            setAgents(mapped);
            setTeams([...teamSet].sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' })));
          }
        }
      } catch {
        /* keep empty */
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [getToken]);

  const campaignOptions = useMemo(() => {
    const titles = visibleCampaignTitlesForStatusSidebar(catalogRows, visibility);
    const titleToId = new Map<string, number>();
    for (const r of catalogRows) {
      const t = r.campaign_title?.trim();
      if (t) titleToId.set(t, r.campaign_id);
    }
    return titles.map((title) => ({
      title,
      campaign_id: titleToId.get(title) ?? null,
    }));
  }, [catalogRows, visibility]);

  const catalogIdToTitle = useMemo(() => {
    const m = new Map<number, string>();
    for (const r of catalogRows) {
      const t = r.campaign_title?.trim();
      if (t) m.set(r.campaign_id, t);
    }
    return m;
  }, [catalogRows]);

  return { campaignOptions, agents, teams, catalogIdToTitle, loading };
}
